import { useState } from "react";
import { AlertTriangle, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useListenerPhoneSettings } from "@/hooks/phone";
import { Agent } from "@/types/agent/agent";
import {
  ListenerPhoneSettings,
  MAX_LISTENER_DISCLOSURE_LENGTH,
  MAX_LISTENER_RING_TO,
  resolveListenerPhoneSettings,
} from "@/types/phone/listener";

const RING_SECONDS_OPTIONS = [15, 20, 25, 30, 45, 60];

interface ListenerCallRoutingProps {
  genie: Agent;
  phoneNumberId: string;
  onSaved?: () => void | Promise<void>;
}

const ListenerCallRouting = ({ genie, phoneNumberId, onSaved }: ListenerCallRoutingProps) => {
  const saved = resolveListenerPhoneSettings(genie.phone?.metadata);
  const [draft, setDraft] = useState<ListenerPhoneSettings>(saved);
  const { saveSettings, isSaving } = useListenerPhoneSettings(genie);

  const update = (patch: Partial<ListenerPhoneSettings>) => setDraft((current) => ({ ...current, ...patch }));

  const setRingTo = (index: number, value: string) =>
    update({ ring_to: draft.ring_to.map((number, i) => (i === index ? value : number)) });

  const handleSave = async () => {
    try {
      const settings = await saveSettings({
        phoneNumberId,
        settings: { ...draft, ring_to: draft.ring_to.map((number) => number.trim()).filter(Boolean) },
      });
      setDraft(settings);
      toast.success("Call routing saved");
      await onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save call routing");
    }
  };

  return (
    <div className="space-y-6 pb-4">
      <section>
        <label className="text-sm font-semibold text-gray-700 mb-1 block">Numbers to ring</label>
        <p className="text-xs text-gray-500 mb-3">
          Every call rings these phones at the same time. The first person to answer takes the call, and your genie
          records and analyses it. Use the international format, for example +64211234567.
        </p>

        <div className="space-y-2">
          {draft.ring_to.map((number, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="tel"
                inputMode="tel"
                value={number}
                onChange={(e) => setRingTo(index, e.target.value)}
                placeholder="+64211234567"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base"
              />
              <button
                type="button"
                onClick={() => update({ ring_to: draft.ring_to.filter((_, i) => i !== index) })}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Remove number"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {draft.ring_to.length < MAX_LISTENER_RING_TO && (
          <button
            type="button"
            onClick={() => update({ ring_to: [...draft.ring_to, ""] })}
            className="mt-3 inline-flex items-center gap-1 text-sm text-genie-primary hover:underline"
          >
            <Plus className="h-4 w-4" />
            Add a number
          </button>
        )}

        {draft.ring_to.length === 0 && (
          <p className="mt-3 text-xs text-gray-500">
            With no numbers to ring, every caller goes straight to voicemail.
          </p>
        )}

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500 mt-0.5" />
          <p className="text-xs text-amber-800">
            If you forward your business number to your genie, don't list that same business number here. The call
            would ring straight back to your genie and loop. Use your team's mobiles, desk phones or another line.
          </p>
        </div>
      </section>

      <section>
        <label className="text-sm font-semibold text-gray-700 mb-2 block" htmlFor="hg-listener-ring-seconds">
          Ring for
        </label>
        <select
          id="hg-listener-ring-seconds"
          value={draft.ring_seconds}
          onChange={(e) => update({ ring_seconds: Number(e.target.value) })}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base"
        >
          {RING_SECONDS_OPTIONS.map((seconds) => (
            <option key={seconds} value={seconds}>
              {seconds} seconds
            </option>
          ))}
        </select>
      </section>

      <section className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-700">Voicemail</p>
          <p className="text-xs text-gray-500 mt-0.5">
            When nobody answers, callers can leave a message. Your genie analyses it like any other call.
          </p>
        </div>
        <Switch
          checked={draft.voicemail_enabled}
          onCheckedChange={(checked) => update({ voicemail_enabled: checked })}
          aria-label="Voicemail"
        />
      </section>

      <section>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-700">Recording notice</p>
            <p className="text-xs text-gray-500 mt-0.5">Played to every caller before the call connects.</p>
          </div>
          <Switch
            checked={draft.disclosure_enabled}
            onCheckedChange={(checked) => update({ disclosure_enabled: checked })}
            aria-label="Recording notice"
          />
        </div>

        {draft.disclosure_enabled ? (
          <div className="mt-3">
            <Textarea
              value={draft.disclosure_text}
              onChange={(e) => update({ disclosure_text: e.target.value })}
              maxLength={MAX_LISTENER_DISCLOSURE_LENGTH}
              rows={3}
              className="rounded-xl text-base"
            />
            <p className="mt-1 text-xs text-gray-500">
              {draft.disclosure_text.length}/{MAX_LISTENER_DISCLOSURE_LENGTH}
            </p>
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500 mt-0.5" />
            <p className="text-xs text-red-700">
              Many countries and states require callers to be told a call is recorded. With the notice off, you are
              responsible for getting the caller's consent.
            </p>
          </div>
        )}
      </section>

      <div className="sticky bottom-0 -mx-8 px-8 py-4 border-t bg-white flex justify-start gap-3">
        <Button variant="outline" onClick={() => setDraft(saved)} disabled={isSaving}>
          Reset
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
        >
          {isSaving ? "Saving..." : "Save call routing"}
        </Button>
      </div>
    </div>
  );
};

export default ListenerCallRouting;
